/**
 * Status diffing utilities
 *
 * Optimizes React re-renders by detecting and updating only units with status changes
 */

import type { Unit } from '@/types';

export interface StatusChange {
  /** `data_changed`: same connection status, but a rendered field moved. */
  type: 'status_changed' | 'data_changed' | 'new';
  unitId: string;
  oldStatus?: 'online' | 'offline' | 'idle' | 'unknown';
  newStatus?: 'online' | 'offline' | 'idle' | 'unknown';
}

/**
 * Detect status changes between current and new unit data
 * Returns only units that have changed status or are new
 * NOTE: Does NOT detect removed units - partial updates should not remove units
 */
export function detectStatusChanges(
  currentUnits: Unit[],
  newStatuses: Array<{
    id: string;
    status: 'online' | 'offline' | 'idle' | 'unknown';
    lastUpdate: string;
    warning_count?: number;
    fault_count?: number;
  }>
): StatusChange[] {
  const changes: StatusChange[] = [];
  const currentMap = new Map(currentUnits.map(t => [t.id, t]));

  for (const newStatus of newStatuses) {
    const current = currentMap.get(newStatus.id);

    if (!current) {
      // New unit
      changes.push({
        type: 'new',
        unitId: newStatus.id,
        newStatus: newStatus.status
      });
    } else if (current.status !== newStatus.status) {
      changes.push({
        type: 'status_changed',
        unitId: newStatus.id,
        oldStatus: current.status,
        newStatus: newStatus.status
      });
    } else if (
      // Detection must cover every field applyStatusChanges writes, or the poll
      // silently no-ops. A healthy unit stays "online" for days while its
      // freshness advances on each sync tick — comparing only `status` left the
      // map card rendering the initial page load's timestamp until a reload.
      current.lastUpdate !== newStatus.lastUpdate ||
      current.warning_count !== newStatus.warning_count ||
      current.fault_count !== newStatus.fault_count
    ) {
      changes.push({
        type: 'data_changed',
        unitId: newStatus.id,
        oldStatus: current.status,
        newStatus: newStatus.status
      });
    }
  }

  return changes;
}

/**
 * Update unit array with status changes
 * Preserves object references for unchanged units (React optimization)
 */
export function applyStatusChanges(
  currentUnits: Unit[],
  changes: StatusChange[],
  newStatuses: Array<{
    id: string;
    status: 'online' | 'offline' | 'idle' | 'unknown';
    lastUpdate: string;
    warning_count?: number;
    fault_count?: number;
  }>
): Unit[] {
  if (changes.length === 0) {
    return currentUnits; // No changes, preserve exact reference
  }

  const changedIds = new Set(changes.map(c => c.unitId));
  const statusMap = new Map(newStatuses.map(s => [s.id, s]));

  return currentUnits.map(unit => {
      if (!changedIds.has(unit.id)) {
        return unit; // Preserve reference for unchanged unit
      }

      const change = changes.find(c => c.unitId === unit.id);
      if (!change) return unit;

      // Get new status data
      const newStatus = statusMap.get(unit.id);
      if (!newStatus) return unit;

      // Create new unit object with updated status
      return {
        ...unit,
        status: newStatus.status,
        lastUpdate: newStatus.lastUpdate,
        warning_count: newStatus.warning_count,
        fault_count: newStatus.fault_count
      };
    });
}

/**
 * Helper to update units with new status data
 * Combines detection and application in one function
 */
export function updateUnitStatuses(
  currentUnits: Unit[],
  newStatuses: Array<{
    id: string;
    status: 'online' | 'offline' | 'idle' | 'unknown';
    lastUpdate: string;
    warning_count?: number;
    fault_count?: number;
  }>
): { units: Unit[]; changedCount: number } {
  const changes = detectStatusChanges(currentUnits, newStatuses);
  const units = applyStatusChanges(currentUnits, changes, newStatuses);

  return {
    units,
    changedCount: changes.length
  };
}
